import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Search, Trash2, Users, UsersRound } from 'lucide-react';
import { api } from '../../lib/api';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { DialogoConfirmar } from '../ui/confirmar';

/*
 * Admin → Grupos: grupos y listas de distribución (contrato en claude/grupos.md).
 *
 * Un grupo sirve en tres sitios y en los tres se resuelve al usarlo: compartir en Drive, asignar
 * casos del buzón a un equipo y escribir a su correo («todos@…»), que reparte el envío entre sus
 * miembros. Por eso aquí basta con decir QUIÉN está: quitar a alguien le quita a la vez los archivos
 * compartidos con el grupo, los casos del equipo y los correos de la lista.
 */

const VACIO = { nombre: '', correo: '', descripcion: '', tipo: 'manual', miembros: [], externos: '', quien_envia: 'organizacion' };

const QUIEN_ENVIA = [
  { id: 'organizacion', label: 'Cualquiera de la organización' },
  { id: 'miembros', label: 'Solo los miembros' },
  { id: 'administradores', label: 'Solo administradores' },
];

function Formulario({ inicial, usuarios, onCerrar, onGuardado }) {
  const [f, setF] = useState(() => ({
    ...VACIO,
    ...inicial,
    correo: inicial?.correo || '',
    externos: (inicial?.externos || []).join('\n'),
    miembros: inicial?.miembros || [],
  }));
  const [q, setQ] = useState('');
  const [guardando, setGuardando] = useState(false);
  const editar = !!inicial?.id;
  const poner = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (usuarios || []).filter((u) => !t || `${u.name} ${u.email}`.toLowerCase().includes(t));
  }, [usuarios, q]);

  const alternar = (id) => poner('miembros', f.miembros.includes(id) ? f.miembros.filter((m) => m !== id) : [...f.miembros, id]);

  const guardar = async () => {
    if (!f.nombre.trim()) {
      toast.message('Ponle un nombre al grupo');
      return;
    }
    setGuardando(true);
    try {
      const cuerpo = {
        ...f,
        correo: f.correo.trim() || null,
        externos: f.externos.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean),
      };
      delete cuerpo.id;
      const { data } = editar ? await api.put(`/admin/grupos/${inicial.id}`, cuerpo) : await api.post('/admin/grupos', cuerpo);
      toast.success(editar ? 'Grupo guardado' : `Grupo «${data.nombre}» creado`);
      onGuardado();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar el grupo');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onCerrar()}>
      <DialogContent className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto" data-testid="grupo-dialogo">
        <DialogHeader>
          <DialogTitle>{editar ? `Editar «${inicial.nombre}»` : 'Nuevo grupo'}</DialogTitle>
          <DialogDescription>
            Sirve para compartir en Drive, asignar casos a un equipo y escribir a todos a la vez.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="grupo-nombre">Nombre</Label>
            <Input id="grupo-nombre" value={f.nombre} onChange={(e) => poner('nombre', e.target.value)} placeholder="Ventas" autoFocus data-testid="grupo-nombre" />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grupo-correo">Correo del grupo (opcional)</Label>
            <Input id="grupo-correo" type="email" value={f.correo} onChange={(e) => poner('correo', e.target.value)} placeholder="ventas@orvian.mx" data-testid="grupo-correo" />
            <p className="text-xs text-muted-foreground">
              Escribir a esta dirección desde Orvian lo reparte entre los miembros. Para que también llegue lo que
              escriben desde fuera, crea en el proveedor un reenvío de esa dirección a los mismos miembros.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="grupo-descripcion">Descripción</Label>
            <Input id="grupo-descripcion" value={f.descripcion} onChange={(e) => poner('descripcion', e.target.value)} placeholder="Para qué es el grupo" />
          </div>

          <fieldset className="grid gap-2">
            <legend className="text-sm font-medium mb-1">Miembros</legend>
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" variant={f.tipo === 'manual' ? 'default' : 'outline'} className="rounded-full" onClick={() => poner('tipo', 'manual')}>
                Elegir personas
              </Button>
              <Button type="button" size="sm" variant={f.tipo === 'organizacion' ? 'default' : 'outline'} className="rounded-full" onClick={() => poner('tipo', 'organizacion')}>
                Toda la organización
              </Button>
            </div>
            {f.tipo === 'organizacion' ? (
              <p className="text-xs text-muted-foreground">
                Todas las cuentas de la organización, también las que entren después. Útil para «todos@».
              </p>
            ) : (
              <div className="border rounded-xl">
                <div className="flex items-center gap-2 px-3 border-b">
                  <Search className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <input
                    className="flex-1 bg-transparent py-2 text-sm outline-none"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar persona"
                    aria-label="Buscar persona"
                  />
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{f.miembros.length} elegidas</span>
                </div>
                <ul className="max-h-56 overflow-auto p-1" aria-label="Personas de la organización">
                  {visibles.map((u) => (
                    <li key={u.id}>
                      <label className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-muted cursor-pointer text-sm">
                        <input type="checkbox" checked={f.miembros.includes(u.id)} onChange={() => alternar(u.id)} data-testid="grupo-miembro" />
                        <span className="min-w-0 flex-1 truncate">{u.name}</span>
                        <span className="text-xs text-muted-foreground truncate">{u.email}</span>
                      </label>
                    </li>
                  ))}
                  {visibles.length === 0 && <li className="px-2 py-3 text-sm text-muted-foreground">Nadie coincide con la búsqueda</li>}
                </ul>
              </div>
            )}
          </fieldset>

          <div className="grid gap-1.5">
            <Label htmlFor="grupo-externos">Correos externos (solo para la lista)</Label>
            <Textarea
              id="grupo-externos"
              rows={2}
              value={f.externos}
              onChange={(e) => poner('externos', e.target.value)}
              placeholder="socio@empresa.com, contador@despacho.mx"
            />
            <p className="text-xs text-muted-foreground">Reciben lo que se escribe al grupo, pero no ven su Drive ni sus casos.</p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="grupo-quien">Quién puede escribir al grupo</Label>
            <select
              id="grupo-quien"
              className="h-10 rounded-md border bg-background px-3 text-sm"
              value={f.quien_envia}
              onChange={(e) => poner('quien_envia', e.target.value)}
            >
              {QUIEN_ENVIA.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={guardar} disabled={guardando} data-testid="grupo-guardar">{guardando ? 'Guardando…' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function PanelGrupos({ usuarios }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['admin-grupos'], queryFn: async () => (await api.get('/grupos')).data.grupos });
  const [editando, setEditando] = useState(null); // null | {} (nuevo) | grupo
  const [borrar, setBorrar] = useState(null);
  const grupos = data || [];

  const recargar = () => qc.invalidateQueries({ queryKey: ['admin-grupos'] });

  const abrir = async (g) => {
    try {
      const { data: detalle } = await api.get(`/grupos/${g.id}`);
      setEditando(detalle);
    } catch {
      toast.error('No se pudo abrir el grupo');
    }
  };

  const eliminar = async (g) => {
    try {
      const { data: r } = await api.delete(`/admin/grupos/${g.id}`);
      const partes = [];
      if (r.accesos_quitados) partes.push(`${r.accesos_quitados} accesos de Drive quitados`);
      if (r.casos_sin_asignar) partes.push(`${r.casos_sin_asignar} casos quedaron sin asignar`);
      toast.success(`Grupo eliminado${partes.length ? ` — ${partes.join(', ')}` : ''}`);
      recargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="font-medium flex items-center gap-2"><UsersRound className="h-4 w-4 text-primary" />Grupos y listas de distribución</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Comparte en Drive con un grupo, asigna casos a un equipo y escribe a todos con una sola dirección.
              Quien entra o sale de un grupo gana o pierde esos accesos al momento.
            </p>
          </div>
          <Button className="gap-1.5" onClick={() => setEditando({})} data-testid="grupo-nuevo"><Plus className="h-4 w-4" />Nuevo grupo</Button>
        </div>
      </Card>
      <Card data-testid="admin-grupos-tabla">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Grupo</TableHead><TableHead>Correo</TableHead><TableHead>Miembros</TableHead><TableHead>Pueden escribir</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Cargando…</TableCell></TableRow>
            ) : grupos.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Aún no hay grupos. Empieza por «Todos» con toda la organización.</TableCell></TableRow>
            ) : grupos.map((g) => (
              <TableRow key={g.id} data-testid="grupo-fila">
                <TableCell>
                  <div className="font-medium">{g.nombre}</div>
                  {g.descripcion && <div className="text-xs text-muted-foreground truncate max-w-[260px]">{g.descripcion}</div>}
                </TableCell>
                <TableCell className="text-sm font-mono">{g.correo || <span className="text-muted-foreground font-sans">—</span>}</TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1.5 text-sm"><Users className="h-3.5 w-3.5" />{g.miembros_n}</span>
                  {g.tipo === 'organizacion' && <Badge variant="secondary" className="ml-2">Toda la organización</Badge>}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{QUIEN_ENVIA.find((o) => o.id === g.quien_envia)?.label}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Editar" aria-label={`Editar ${g.nombre}`} onClick={() => abrir(g)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" title="Eliminar" aria-label={`Eliminar ${g.nombre}`} onClick={() => setBorrar(g)}><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {editando && (
        <Formulario
          inicial={editando}
          usuarios={usuarios}
          onCerrar={() => setEditando(null)}
          onGuardado={() => { setEditando(null); recargar(); }}
        />
      )}
      <DialogoConfirmar
        open={!!borrar}
        onOpenChange={(v) => !v && setBorrar(null)}
        titulo={`¿Eliminar el grupo «${borrar?.nombre}»?`}
        mensaje="Sus miembros pierden lo que se compartió con el grupo en Drive, los casos asignados al equipo quedan sin asignar y su correo deja de repartir. Las personas y sus buzones no se tocan."
        boton="Eliminar grupo"
        testId="grupo-confirmar-eliminar"
        onConfirmar={() => eliminar(borrar)}
      />
    </div>
  );
}
